import { supabase } from './supabase';
import type { RegistryStats, AdminDashboardStats, AdminPayHereStats, UserStats, AdminVendor, AdminVenue, VendorFormData, VenueFormData, WithdrawalRequest, RegistryWithdrawalRequest, InvoiceData } from '../types/admin';

export async function isAdmin(): Promise<boolean> {
  try {
    const { data, error } = await supabase.rpc('is_admin');
    if (error) throw error;
    return data || false;
  } catch (error) {
    console.error('Error checking admin status:', error);
    return false;
  }
}

export async function getRegistryStats(): Promise<RegistryStats[]> {
  try {
    const { data, error } = await supabase.rpc('get_admin_registry_stats');
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching registry stats:', error);
    throw error;
  }
}

export async function getUserStats(): Promise<UserStats[]> {
  try {
    const { data, error } = await supabase.rpc('get_admin_user_stats');
    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching user stats:', error);
    throw error;
  }
}

export async function getPayHereCommissionStats(): Promise<AdminPayHereStats> {
  try {
    const { data, error } = await supabase.rpc('get_payhere_payment_stats');
    if (error) throw error;
    const stats = data || {};
    return {
      total_transactions: stats.total_transactions || 0,
      total_commission: Number(stats.total_commission) || 0,
      total_gateway_fees: Number(stats.total_gateway_fees) || 0,
      total_volume: Number(stats.total_volume) || 0,
      net_transferable: Number(stats.net_transferable) || 0,
      total_guest_fee_share: Number(stats.total_guest_fee_share) || 0,
      total_couple_fee_share: Number(stats.total_couple_fee_share) || 0,
      guest_pays_count: stats.guest_pays_count || 0,
      couple_absorbs_count: stats.couple_absorbs_count || 0,
      split_count: stats.split_count || 0,
      commission_from_guest_pays: Number(stats.commission_from_guest_pays) || 0,
      commission_from_couple_absorbs: Number(stats.commission_from_couple_absorbs) || 0,
      commission_from_splits: Number(stats.commission_from_splits) || 0,
      registries_guest_pays: stats.registries_guest_pays || 0,
      registries_couple_absorbs: stats.registries_couple_absorbs || 0,
      registries_split: stats.registries_split || 0,
    };
  } catch (error) {
    console.error('Error fetching PayHere stats:', error);
    return { total_transactions: 0, total_commission: 0, total_gateway_fees: 0, total_volume: 0, net_transferable: 0 };
  }
}

export async function getDashboardStats(): Promise<AdminDashboardStats> {
  try {
    const [registries, payhereStats] = await Promise.all([
      getRegistryStats(),
      getPayHereCommissionStats()
    ]);

    const totalRegistries = registries.length;
    const totalCollected = registries.reduce((sum, reg) => sum + reg.total_collected, 0);
    const activeUsers = new Set(registries.map(reg => reg.owner_email)).size;

    return {
      total_registries: totalRegistries,
      total_collected: totalCollected,
      total_transactions: payhereStats.total_transactions,
      total_commission: payhereStats.total_commission,
      total_gateway_fees: payhereStats.total_gateway_fees,
      net_transferable: payhereStats.net_transferable,
      active_users: activeUsers,
      total_guest_fee_share: payhereStats.total_guest_fee_share,
      total_couple_fee_share: payhereStats.total_couple_fee_share,
      guest_pays_count: payhereStats.guest_pays_count,
      couple_absorbs_count: payhereStats.couple_absorbs_count,
      split_count: payhereStats.split_count,
      commission_from_guest_pays: payhereStats.commission_from_guest_pays,
      commission_from_couple_absorbs: payhereStats.commission_from_couple_absorbs,
      commission_from_splits: payhereStats.commission_from_splits,
      registries_guest_pays: payhereStats.registries_guest_pays,
      registries_couple_absorbs: payhereStats.registries_couple_absorbs,
      registries_split: payhereStats.registries_split,
    };
  } catch (error) {
    console.error('Error fetching dashboard stats:', error);
    throw error;
  }
}

export async function deleteRegistry(registryId: string, reason?: string): Promise<void> {
  try {
    const { data: registry, error: fetchError } = await supabase
      .from('registries')
      .select('title, user_id')
      .eq('id', registryId)
      .single();

    if (fetchError) throw fetchError;

    const { error } = await supabase
      .from('registries')
      .delete()
      .eq('id', registryId);

    if (error) throw error;

    await supabase.rpc('log_admin_action', {
      action_type_param: 'delete_registry',
      target_type_param: 'registry',
      target_id_param: registryId,
      old_values_param: { title: registry.title, user_id: registry.user_id },
      new_values_param: { deleted: true, reason: reason || 'No reason provided' }
    });
  } catch (error) {
    console.error('Error deleting registry:', error);
    throw error;
  }
}

export async function deleteUser(userId: string, reason?: string): Promise<void> {
  try {
    const { error } = await supabase.rpc('delete_user_and_data', {
      target_user_id: userId,
      deletion_reason: reason
    });

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting user:', error);
    throw error;
  }
}

export async function exportRegistryData(): Promise<void> {
  try {
    const data = await getRegistryStats();

    const csvContent = [
      ['Registry ID', 'Owner Name', 'Owner Email', 'Contact', 'Title', 'Created Date', 'Total Collected'].join(','),
      ...data.map(row => [
        row.registry_id,
        `"${row.owner_name}"`,
        row.owner_email,
        `"${row.contact_number || ''}"`,
        `"${row.title}"`,
        new Date(row.created_at).toLocaleDateString(),
        row.total_collected
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `registry_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting data:', error);
    throw error;
  }
}

export async function exportUserData(): Promise<void> {
  try {
    const data = await getUserStats();

    const csvContent = [
      ['User ID', 'Email', 'Full Name', 'Username', 'Created Date', 'Last Sign In', 'Registry Count', 'Total Collected'].join(','),
      ...data.map(row => [
        row.user_id,
        row.email,
        `"${row.full_name}"`,
        `"${row.username}"`,
        new Date(row.created_at).toLocaleDateString(),
        row.last_sign_in_at ? new Date(row.last_sign_in_at).toLocaleDateString() : 'Never',
        row.registry_count,
        row.total_collected
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `user_data_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting user data:', error);
    throw error;
  }
}

export async function updateFeeSettings(platformFeeRate: number, gatewayFeeRate: number): Promise<void> {
  try {
    const { error: platformError } = await supabase
      .from('system_settings')
      .update({ value: platformFeeRate.toString() })
      .eq('key', 'platform_fee_rate');

    if (platformError) throw platformError;

    const { error: gatewayError } = await supabase
      .from('system_settings')
      .update({ value: gatewayFeeRate.toString() })
      .eq('key', 'gateway_fee_rate');

    if (gatewayError) throw gatewayError;

    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/update-registry-fees`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({ platformFeeRate, gatewayFeeRate })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(`Failed to update registries via Edge Function: ${errorData.error || response.statusText}`);
    }
  } catch (error) {
    console.error('Error updating fee settings:', error);
    throw error;
  }
}

export async function getFeeSettings(): Promise<{ platformFeeRate: number; gatewayFeeRate: number }> {
  try {
    const { data: platformData, error: platformError } = await supabase
      .from('system_settings')
      .select('value')
      .eq('key', 'platform_fee_rate')
      .single();

    if (platformError) throw platformError;

    const { data: gatewayData, error: gatewayError } = await supabase
      .from('system_settings')
      .select('value')
      .eq('key', 'gateway_fee_rate')
      .single();

    if (gatewayError) throw gatewayError;

    return {
      platformFeeRate: parseFloat(platformData.value),
      gatewayFeeRate: parseFloat(gatewayData.value)
    };
  } catch (error) {
    console.error('Error getting fee settings:', error);
    throw error;
  }
}

export async function resetUserPassword(userId: string, reason?: string): Promise<{ email: string; newPassword: string }> {
  try {
    const { data: { session } } = await supabase.auth.getSession();
    const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/reset-user-password`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session?.access_token || import.meta.env.VITE_SUPABASE_ANON_KEY}`
      },
      body: JSON.stringify({ userId, reason })
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(errorData.error || 'Failed to reset user password');
    }

    const result = await response.json();
    return { email: result.email, newPassword: result.newPassword };
  } catch (error) {
    console.error('Error resetting user password:', error);
    throw error;
  }
}

// Vendor Management Functions
export async function getAdminVendors(): Promise<AdminVendor[]> {
  try {
    const { data, error } = await supabase
      .from('admin_vendors')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching admin vendors:', error);
    throw error;
  }
}

export async function createAdminVendor(vendorData: VendorFormData): Promise<AdminVendor> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('admin_vendors')
      .insert([{
        ...vendorData,
        is_active: true,
        created_by: user?.id || null,
      }])
      .select();

    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error('Vendor was not returned after insert');
    }
    return data[0];
  } catch (error) {
    console.error('Error creating admin vendor:', error);
    throw error;
  }
}

export async function updateAdminVendor(vendorId: string, updates: Partial<VendorFormData>): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_vendors')
      .update(updates)
      .eq('id', vendorId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating admin vendor:', error);
    throw error;
  }
}

export async function deleteAdminVendor(vendorId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_vendors')
      .delete()
      .eq('id', vendorId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting admin vendor:', error);
    throw error;
  }
}

export async function toggleVendorStatus(vendorId: string, isActive: boolean): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_vendors')
      .update({ is_active: isActive })
      .eq('id', vendorId);

    if (error) throw error;
  } catch (error) {
    console.error('Error toggling vendor status:', error);
    throw error;
  }
}

// Venue Management Functions
export async function getAdminVenues(): Promise<AdminVenue[]> {
  try {
    const { data, error } = await supabase
      .from('admin_venues')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching admin venues:', error);
    throw error;
  }
}

export async function createAdminVenue(venueData: VenueFormData): Promise<AdminVenue> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    const { data, error } = await supabase
      .from('admin_venues')
      .insert([{
        ...venueData,
        status: 'active',
        is_active: true,
        created_by: user?.id || null,
      }])
      .select();

    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error('Venue was not returned after insert');
    }
    return data[0];
  } catch (error) {
    console.error('Error creating admin venue:', error);
    throw error;
  }
}

export async function updateAdminVenue(venueId: string, updates: Partial<VenueFormData>): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_venues')
      .update(updates)
      .eq('id', venueId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating admin venue:', error);
    throw error;
  }
}

export async function deleteAdminVenue(venueId: string): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_venues')
      .delete()
      .eq('id', venueId);

    if (error) throw error;
  } catch (error) {
    console.error('Error deleting admin venue:', error);
    throw error;
  }
}

export async function updateVenueStatus(venueId: string, status: 'active' | 'inactive' | 'maintenance'): Promise<void> {
  try {
    const { error } = await supabase
      .from('admin_venues')
      .update({ status, is_active: status === 'active' })
      .eq('id', venueId);

    if (error) throw error;
  } catch (error) {
    console.error('Error updating venue status:', error);
    throw error;
  }
}

// Public Data Functions (No auth required)
export async function getPublicVendors(): Promise<AdminVendor[]> {
  try {
    const { data, error } = await supabase
      .from('admin_vendors')
      .select('*')
      .eq('is_active', true)
      .order('rating', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching public vendors:', error);
    return [];
  }
}

export async function getPublicVenues(): Promise<AdminVenue[]> {
  try {
    const { data, error } = await supabase
      .from('admin_venues')
      .select('*')
      .eq('status', 'active')
      .eq('is_active', true)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  } catch (error) {
    console.error('Error fetching public venues:', error);
    return [];
  }
}

export async function exportVendorData(): Promise<void> {
  try {
    const vendors = await getAdminVendors();

    const csvContent = [
      ['ID', 'Name', 'Contact Person', 'Email', 'Phone', 'Address', 'Website', 'Service Categories', 'Rating', 'Status', 'Created Date'].join(','),
      ...vendors.map(vendor => [
        vendor.id,
        `"${vendor.name}"`,
        `"${vendor.contact_person}"`,
        vendor.email,
        vendor.phone,
        `"${vendor.address}"`,
        vendor.website || '',
        `"${vendor.service_categories.join('; ')}"`,
        vendor.rating,
        vendor.is_active ? 'Active' : 'Inactive',
        new Date(vendor.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `admin_vendors_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting vendor data:', error);
    throw error;
  }
}

export async function exportVenueData(): Promise<void> {
  try {
    const venues = await getAdminVenues();

    const csvContent = [
      ['ID', 'Name', 'Address', 'Contact Email', 'Contact Phone', 'Max Capacity', 'Amenities', 'Status', 'Created Date'].join(','),
      ...venues.map(venue => [
        venue.id,
        `"${venue.name}"`,
        `"${venue.address}"`,
        venue.contact_email,
        venue.contact_phone,
        venue.capacity_details?.max_capacity || 0,
        `"${venue.amenities.join('; ')}"`,
        venue.status,
        new Date(venue.created_at).toLocaleDateString()
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `admin_venues_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error exporting venue data:', error);
    throw error;
  }
}

// Withdrawal Management Functions
export async function getAdminWithdrawalRequests(): Promise<WithdrawalRequest[]> {
  try {
    const { data, error } = await supabase.rpc('get_admin_withdrawal_requests');
    if (error) throw error;
    return (data || []) as WithdrawalRequest[];
  } catch (error) {
    console.error('Error fetching withdrawal requests:', error);
    throw error;
  }
}

export async function approveWithdrawalRequest(requestId: string): Promise<void> {
  try {
    const { error } = await supabase.rpc('approve_withdrawal_request', { request_id: requestId });
    if (error) throw error;
  } catch (error) {
    console.error('Error approving withdrawal request:', error);
    throw error;
  }
}

export async function completeWithdrawalRequest(
  requestId: string,
  transferReference: string,
  closeRegistry: boolean
): Promise<void> {
  try {
    const { error } = await supabase.rpc('complete_withdrawal_request', {
      request_id: requestId,
      transfer_ref: transferReference,
      close_registry: closeRegistry,
    });
    if (error) throw error;
  } catch (error) {
    console.error('Error completing withdrawal request:', error);
    throw error;
  }
}

export async function getRegistryWithdrawalRequests(registryId: string): Promise<RegistryWithdrawalRequest[]> {
  try {
    const { data, error } = await supabase.rpc('get_registry_withdrawal_requests', {
      registry_id_param: registryId,
    });
    if (error) throw error;
    return (data || []) as RegistryWithdrawalRequest[];
  } catch (error) {
    console.error('Error fetching registry withdrawal requests:', error);
    throw error;
  }
}

export async function createWithdrawalRequest(
  registryId: string,
  amount: number
): Promise<void> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('You must be signed in to request a withdrawal');

    const { error } = await supabase.from('withdrawal_requests').insert({
      registry_id: registryId,
      user_id: user.id,
      amount,
      status: 'pending',
    });
    if (error) throw error;
  } catch (error) {
    console.error('Error creating withdrawal request:', error);
    throw error;
  }
}

export async function getRegistryPayoutBreakdown(registryId: string): Promise<InvoiceData> {
  try {
    const { data, error } = await supabase.rpc('get_registry_payout_breakdown', {
      registry_uuid: registryId,
    });
    if (error) throw error;
    if (!data) throw new Error('No data returned from payout breakdown');
    if (data.error) throw new Error(data.error);

    return {
      registry_id: data.registry_id,
      title: data.title,
      owner_name: data.owner_name,
      owner_email: data.owner_email,
      contact_number: data.contact_number || '',
      delivery_address: data.delivery_address || '',
      fee_responsibility: data.fee_responsibility || 'GUEST',
      platform_fee_percentage: Number(data.platform_fee_percentage) || 0,
      gateway_fee_percentage: Number(data.gateway_fee_percentage) || 0,
      transaction_count: data.transaction_count || 0,
      gross_gift_amount: Number(data.gross_gift_amount) || 0,
      platform_fee_total: Number(data.platform_fee_total) || 0,
      gateway_fee_total: Number(data.gateway_fee_total) || 0,
      guest_fee_share_total: Number(data.guest_fee_share_total) || 0,
      couple_fee_share_total: Number(data.couple_fee_share_total) || 0,
      total_fees: Number(data.total_fees) || 0,
      net_payable: Number(data.net_payable) || 0,
      total_withdrawn: Number(data.total_withdrawn) || 0,
      remaining_balance: Number(data.remaining_balance) || 0,
      transactions: data.transactions || [],
    };
  } catch (error) {
    console.error('Error fetching registry payout breakdown:', error);
    throw error;
  }
}
